<template>
  <div class="container mx-auto px-4 py-8 max-w-6xl">
    <div class="mb-8 flex flex-row">
      <div class="flex-1">
        <h1 class="text-3xl font-bold text-gray-900">좌석 예약 게시판</h1>
        <p class="mt-2 text-gray-600">다양한 장소의 좌석을 예약하고 관리하세요</p>
      </div>
      <div class="flex-2">
        <RouterLink v-if="isAuthenticated" to="/posts/create" class="btn btn-primary btn-sm">
          게시글 작성
        </RouterLink>
      </div>
    </div>
    <div class="grid gap-8 lg:grid-cols-[260px_1fr]">
      <aside class="space-y-6 lg:sticky lg:top-24 lg:self-start">
        <button
          class="btn btn-outline btn-sm flex items-center gap-2 w-full justify-center lg:hidden"
          @click="showFilters = !showFilters"
        >
          <FunnelIcon class="w-4 h-4" />
          {{ showFilters ? '필터 닫기' : '필터 열기' }}
        </button>
        <div :class="showFilters ? 'block' : 'hidden lg:block'">
          <BoardFilterPanel>
            <div class="space-y-4">
              <div>
                <p class="text-xs font-semibold text-gray-500">예약 상태</p>
                <div class="mt-2 space-y-2 text-sm text-gray-600">
                  <label class="flex items-center gap-2">
                    <input v-model="filters.status" type="radio" value="all" class="text-blue-600" />
                    전체
                  </label>
                  <label class="flex items-center gap-2">
                    <input v-model="filters.status" type="radio" value="available" class="text-blue-600" />
                    예약 가능
                  </label>
                  <label class="flex items-center gap-2">
                    <input v-model="filters.status" type="radio" value="upcoming" class="text-blue-600" />
                    예약 전
                  </label>
                  <label class="flex items-center gap-2">
                    <input v-model="filters.status" type="radio" value="full" class="text-blue-600" />
                    만석
                  </label>
                  <label class="flex items-center gap-2">
                    <input v-model="filters.status" type="radio" value="closed" class="text-blue-600" />
                    예약 마감
                  </label>
                </div>
              </div>

              <div>
                <p class="text-xs font-semibold text-gray-500">예약 기간</p>
                <div class="mt-2 space-y-2">
                  <input v-model="filters.startDate" type="date" class="form-input text-sm" />
                  <input v-model="filters.endDate" type="date" class="form-input text-sm" />
                </div>
              </div>

              <div>
                <p class="text-xs font-semibold text-gray-500">남은 좌석</p>
                <select v-model="filters.minAvailableSeats" class="form-input text-sm">
                  <option value="all">전체</option>
                  <option value="1">1석 이상</option>
                  <option value="5">5석 이상</option>
                  <option value="10">10석 이상</option>
                </select>
              </div>

              <button class="btn btn-outline btn-sm w-full" @click="resetFilters">
                필터 초기화
              </button>
            </div>
          </BoardFilterPanel>
        </div>
      </aside>

      <section class="space-y-6">
        <BoardSearchBar
          v-model="searchKeyword"
          placeholder="장소명, 주소로 검색..."
          :show-button="true"
          :disabled="isLoading"
          @search="search"
        />

        <BoardListToolbar
          v-model:sortBy="sortBy"
          v-model:viewMode="viewMode"
          v-model:pageSize="pageSize"
          :sort-options="sortOptions"
          :total-elements="totalElements"
          :filtered-elements="filteredPosts.length !== posts.length ? filteredPosts.length : null"
          :page-size-options="pageSizeOptions"
          :show-sort="true"
          :show-view-mode="true"
          :show-page-size="true"
          :show-filtered-count="true"
          @sort-change="loadPosts(0)"
          @page-size-change="loadPosts(0)"
        />

        <div v-if="isLoading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          <PostCardSkeleton v-for="n in 6" :key="n" />
        </div>

        <div v-else-if="filteredPosts.length > 0">
          <div v-if="viewMode === 'grid'" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            <PostCard
              v-for="post in filteredPosts"
              :key="post.id"
              :post="post"
              :seat-layout-info="getSeatLayoutInfoByPost(post)"
              :reserved-seat-numbers="reservedSeatNumbersByPost[post.id]"
              class="hover:shadow-lg transition-shadow"
            />
          </div>
          <div v-else class="space-y-4">
            <PostListItem
              v-for="post in filteredPosts"
              :key="post.id"
              :post="post"
              :seat-layout-info="getSeatLayoutInfoByPost(post)"
              :reserved-seat-numbers="reservedSeatNumbersByPost[post.id]"
            />
          </div>
        </div>

        <div v-else class="text-center py-16">
          <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <DocumentTextIcon class="w-8 h-8 text-gray-400" />
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-2">
            {{ hasActiveFilters || searchKeyword ? '조건에 맞는 게시글이 없습니다' : '등록된 게시글이 없습니다' }}
          </h3>
          <p class="text-gray-600 mb-6">
            {{
              hasActiveFilters || searchKeyword
                ? '검색어나 필터 조건을 조정해보세요.'
                : '첫 번째 게시글을 작성해보세요.'
            }}
          </p>
          <div class="space-x-3">
            <button v-if="searchKeyword" @click="clearSearch" class="btn btn-outline">
              검색 초기화
            </button>
            <button v-if="hasActiveFilters" @click="resetFilters" class="btn btn-outline">
              필터 초기화
            </button>
          </div>
        </div>

        <div v-if="totalPages > 1" class="mt-8">
          <Pagination :current-page="currentPage" :total-pages="totalPages" @page-change="goToPage" />
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, reactive, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { DocumentTextIcon, FunnelIcon } from '@heroicons/vue/24/outline'
import { postApi, reservationApi } from '@/services/api'
import type { Post, SeatLayoutInfo } from '@/types'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'
import PostCard from '@/components/PostCard.vue'
import PostListItem from '@/components/PostListItem.vue'
import PostCardSkeleton from '@/components/PostCardSkeleton.vue'
import Pagination from '@/components/common/Pagination.vue'
import BoardSearchBar from '@/components/common/BoardSearchBar.vue'
import BoardListToolbar from '@/components/common/BoardListToolbar.vue'
import BoardFilterPanel from '@/components/common/BoardFilterPanel.vue'
import { deserializeSeatStatus } from '@/utils'

const route = useRoute()
const authStore = useAuthStore()
const toast = useToast()

const isAuthenticated = computed(() => authStore.isAuthenticated)

const posts = ref<Post[]>([])
const reservedSeatNumbersByPost = ref<Record<number, number[]>>({})
const isLoading = ref(true)
const searchKeyword = ref('')
const sortBy = ref('createdAt,desc')
const viewMode = ref<'grid' | 'list'>('list')
const showFilters = ref(false)

const currentPage = ref(0)
const totalPages = ref(0)
const totalElements = ref(0)

const pageSizeOptions = [6, 12, 18, 21]
const pageSize = ref(6)

const sortOptions = [
  { value: 'createdAt,desc', label: '최신순' },
  { value: 'createdAt,asc', label: '오래된순' },
  { value: 'startDate,asc', label: '시작일 빠른순' },
  { value: 'title,asc', label: '제목순' }
]

const filters = reactive({
  status: 'all' as 'all' | 'available' | 'full' | 'upcoming' | 'closed',
  startDate: '',
  endDate: '',
  minAvailableSeats: 'all'
})

const preferenceStorageKey = 'postListPreferences'

const persistPreferences = () => {
  if (typeof window === 'undefined') return
  const payload = {
    sortBy: sortBy.value,
    viewMode: viewMode.value,
    pageSize: pageSize.value,
    filters: {
      status: filters.status,
      startDate: filters.startDate,
      endDate: filters.endDate,
      minAvailableSeats: filters.minAvailableSeats
    }
  }
  sessionStorage.setItem(preferenceStorageKey, JSON.stringify(payload))
}

const applyPreferences = () => {
  if (typeof window === 'undefined') return
  const raw = sessionStorage.getItem(preferenceStorageKey)
  if (!raw) return

  try {
    const stored = JSON.parse(raw)
    if (typeof stored.sortBy === 'string') {
      sortBy.value = stored.sortBy
    }
    if (stored.viewMode === 'grid' || stored.viewMode === 'list') {
      viewMode.value = stored.viewMode
    }
    if (pageSizeOptions.includes(Number(stored.pageSize))) {
      pageSize.value = Number(stored.pageSize)
    }
    if (stored.filters) {
      if (['all', 'available', 'full', 'upcoming', 'closed'].includes(stored.filters.status)) {
        filters.status = stored.filters.status
      }
      if (typeof stored.filters.startDate === 'string') {
        filters.startDate = stored.filters.startDate
      }
      if (typeof stored.filters.endDate === 'string') {
        filters.endDate = stored.filters.endDate
      }
      if (['all', '1', '5', '10'].includes(stored.filters.minAvailableSeats)) {
        filters.minAvailableSeats = stored.filters.minAvailableSeats
      }
    }
  } catch (error) {
    console.warn('Failed to parse saved preferences:', error)
  }
}

const hasActiveFilters = computed(() => {
  return (
    filters.status !== 'all' ||
    filters.startDate !== '' ||
    filters.endDate !== '' ||
    filters.minAvailableSeats !== 'all'
  )
})

const getPostStatus = (post: Post) => {
  const now = new Date()
  const startDate = new Date(post.startDate)
  const endDate = new Date(post.endDate)
  const totalSeats = post.totalSeats || 0
  const reservedSeats = post.reservedSeats || 0
  const hasAvailableSeats = totalSeats > reservedSeats

  if (now < startDate) return 'upcoming'
  if (now > endDate) return 'closed'
  return hasAvailableSeats ? 'available' : 'full'
}

const filteredPosts = computed(() => {
  return posts.value.filter((post) => {
    const status = getPostStatus(post)
    if (filters.status !== 'all' && status !== filters.status) return false

    if (filters.startDate) {
      const filterStart = new Date(`${filters.startDate}T00:00:00`)
      if (new Date(post.startDate) < filterStart) return false
    }

    if (filters.endDate) {
      const filterEnd = new Date(`${filters.endDate}T23:59:59`)
      if (new Date(post.endDate) > filterEnd) return false
    }

    if (filters.minAvailableSeats !== 'all') {
      const availableSeats = Math.max((post.totalSeats || 0) - (post.reservedSeats || 0), 0)
      if (availableSeats < Number(filters.minAvailableSeats)) return false
    }

    return true
  })
})

const getSeatLayoutInfoByPost = (post: Post) => {
  const seatStatus: number[][] = deserializeSeatStatus(post.seatLayout)
  const rows: number = seatStatus.length
  const seatsPerRow: number = seatStatus[0]?.length ?? 0
  let totalSeats = 0
  seatStatus.forEach((row) => row.forEach((seat) => { if (seat === 1) totalSeats += 1 }))

  const seatLayoutInfo: SeatLayoutInfo = {
    rows,
    seatsPerRow,
    totalSeats,
    seatStatus
  }

  return seatLayoutInfo
}

const resetFilters = () => {
  filters.status = 'all'
  filters.startDate = ''
  filters.endDate = ''
  filters.minAvailableSeats = 'all'
}

const loadPosts = async (page: number = 0) => {
  try {
    isLoading.value = true
    const [sort, direction] = sortBy.value.split(',')

    const params: any = {
      page,
      size: pageSize.value,
      sort: `${sort},${direction}`
    }

    if (searchKeyword.value.trim()) {
      params.keyword = searchKeyword.value.trim()
    }

    const response = await postApi.getPosts(params)

    posts.value = response.content
    currentPage.value = response.number
    totalPages.value = response.totalPages
    totalElements.value = response.totalElements

    if (posts.value.length > 0) {
      try {
        const seatStatusResponse = await reservationApi.getPostSeatStatuses(
          posts.value.map((post) => post.id)
        )
        const seatMap: Record<number, number[]> = {}
        seatStatusResponse.data.forEach((status) => {
          seatMap[status.postId] = status.reservedSeatNumbers || []
        })
        reservedSeatNumbersByPost.value = seatMap
      } catch (error) {
        console.error('Failed to load seat status:', error)
        reservedSeatNumbersByPost.value = {}
      }
    } else {
      reservedSeatNumbersByPost.value = {}
    }
  } catch (error) {
    console.error('Failed to load posts:', error)
    toast.error('게시글 목록을 불러오는데 실패했습니다.')
  } finally {
    isLoading.value = false
  }
}

const search = () => {
  loadPosts(0)
}

const clearSearch = () => {
  searchKeyword.value = ''
  loadPosts(0)
}

const goToPage = (page: number) => {
  loadPosts(page)
}

const initFromQuery = () => {
  const query = route.query

  if (query.q && typeof query.q === 'string') {
    searchKeyword.value = query.q
  }

  if (query.sort && typeof query.sort === 'string') {
    sortBy.value = query.sort
  }

  const page = query.page ? parseInt(query.page.toString(), 10) - 1 : 0
  loadPosts(Math.max(0, page))
}

onMounted(() => {
  applyPreferences()
  initFromQuery()
})

watch([sortBy, viewMode, pageSize], () => {
  persistPreferences()
})

watch(
  filters,
  () => {
    persistPreferences()
  },
  { deep: true }
)
</script>
